import React from "react";
import PropTypes from "prop-types";
import countries from "i18n-iso-countries";
import enLocale from "i18n-iso-countries/langs/en.json";
import AutocompleteQuestion from "./AutocompleteQuestion";

// Register locale once at module load time
countries.registerLocale(enLocale);

// Module-level cache for country options
let cachedCountryOptions = null;

// Search aliases - allow users to find countries by common alternative names.
// Entries below are only needed where match-sorter's STARTS_WITH and
// WORD_STARTS_WITH ranks won't already surface the country from the typed
// query against the canonical i18n-iso-countries label. For example "Russia"
// finds "Russian Federation" via STARTS_WITH and needs no alias, but "Turkey"
// won't reach "Türkiye" by any rank and does.
const COUNTRY_ALIASES = {
  GBR: ["England", "Wales", "Scotland", "Britain", "UK", "Great Britain"],
  USA: ["America", "US", "United States"],
  IRL: ["Eire"],
  ARE: ["UAE"], // United Arab Emirates
  CIV: ["Ivory Coast"], // Cote d'Ivoire
  COD: ["DRC"], // Democratic Republic of the Congo
  LAO: ["Laos"], // Lao People's Democratic Republic
  MAC: ["Macau"], // Macao
  MMR: ["Burma"], // Myanmar
  TUR: ["Turkey"], // Türkiye
  VGB: ["British Virgin Islands", "BVI"], // Virgin Islands, British
  VIR: ["US Virgin Islands", "USVI"], // Virgin Islands, U.S.
};

/**
 * Keep match-sorter's relevance order, then pin GBR to the top if present.
 *
 * IMPORTANT: This function depends on match-sorter's relevance ordering being preserved.
 * When filtering, match-sorter returns results ordered by relevance (best matches first).
 * This function takes that ordered list and simply moves GBR to the top if present,
 * keeping all other items in their original relevance order.
 *
 * If match-sorter's sorting behavior changes, this may need adjustment.
 */
function sortCountries(filteredOptions) {
  const gbr = filteredOptions.find((opt) => opt.value === "GBR");
  if (!gbr) return filteredOptions; // nothing to pin
  const rest = filteredOptions.filter((opt) => opt.value !== "GBR");
  return [gbr, ...rest];
}

/**
 * Generate country options with Alpha-3 codes.
 * Results are cached for performance.
 */
function getCountryOptions() {
  if (cachedCountryOptions) {
    return [...cachedCountryOptions];
  }

  const alpha3Codes = Object.keys(countries.getAlpha3Codes());
  const options = alpha3Codes
    .map((code) => ({
      value: code,
      label: countries.getName(code, "en"),
      aliases: COUNTRY_ALIASES[code] || [],
    }))
    .filter((opt) => opt.label);

  // Sort: GBR first, then alphabetical
  // Order shown when dropdown is open with no typing
  options.sort((a, b) => {
    if (a.value === "GBR") return -1;
    if (b.value === "GBR") return 1;
    return a.label.localeCompare(b.label, "en", { sensitivity: "base" });
  });

  cachedCountryOptions = options;
  return [...cachedCountryOptions];
}

/**
 * Test helper to clear cache.
 * @private
 */
export const __clearCountryCache = () => {
  cachedCountryOptions = null;
};

/**
 * Country picker component using MUI Base UI components.
 *
 * Provides a searchable dropdown of countries with Alpha-3 country codes.
 * This is a thin wrapper around AutocompleteQuestion that provides country-specific
 * configuration.
 *
 * Features:
 * - Supports country aliases (e.g., typing "England" finds "United Kingdom")
 * - GBR (United Kingdom) is prioritized at the top of filtered results
 * - Caches country options for performance
 *
 * Value handling:
 * - Accepts and emits Alpha-3 country codes as strings (e.g., "GBR", "USA")
 * - AutocompleteQuestion handles the conversion to/from option objects internally
 *
 */
function CountryPicker({
  value,
  onChange,
  placeholder = "Start typing a country name...",
  question,
  hint,
  optional,
  validationError,
  validate,
  disabled,
  id,
}) {
  const options = getCountryOptions();

  return (
    <AutocompleteQuestion
      id={id}
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      question={question}
      hint={hint}
      optional={optional}
      validationError={validationError}
      validate={validate}
      disabled={disabled}
      autoHighlight={true}
      sortOptions={sortCountries}
      filterKeys={["label", "aliases"]}
      noOptionsText="No matching countries"
    />
  );
}

CountryPicker.propTypes = {
  /**
   * Currently selected country code (Alpha-3 format, e.g., "GBR").
   */
  value: PropTypes.string,
  /**
   * Callback when country selection changes - receives Alpha-3 code string.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Placeholder text for the input.
   */
  placeholder: PropTypes.string,
  /**
   * Label text for the field.
   */
  question: PropTypes.string,
  /**
   * Hint text displayed below the label.
   */
  hint: PropTypes.string,
  /**
   * Whether the field is optional.
   */
  optional: PropTypes.bool,
  /**
   * Validation error message to display.
   */
  validationError: PropTypes.string,
  /**
   * Validation callback triggered on blur/focus.
   */
  validate: PropTypes.func,
  /**
   * Whether the field is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Custom ID for the input element (generated if not provided).
   */
  id: PropTypes.string,
};

export default CountryPicker;
